import { useCallback, useEffect, useState } from "react";
import { Autocomplete, Box, Button, FormControl, Grid, TextField, Typography } from "@mui/material";
import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import BreadcrumbBar from "../../../layout/BreadcrumbBar";
import { EditarAcuerdo, FlujoAcuerdos, obtenerDocumentoAcuerdos } from "../../../services/PpafeServices";
import Swal from "sweetalert2";
import axios from "axios";
import { puedeActualizar } from "../../../utils/permisosFlujo";
import { flujoEnum } from "../../../types/enumTypes";

interface TipoPublicacion {
    id: string;
    nombre: string;
    estatus: string;
    fechaAlta: string;
    fechaBaja: string | null;
    creadoPorId: string | null;
}

const VerifiacionAcuerdosPF = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [responseData, setResponseData] = useState<any[]>([]);
    const [titulo, setTitulo] = useState<string>("");
    const [tipoPublicacion, setTipoPublicacion] = useState<string>("");
    const [pdfUrl, setPdfUrl] = useState<string | null>(null);
    const [catalogoTiposPublicacion, setCatalogoTiposPublicacion] = useState<TipoPublicacion[]>([]);
    const [loadingCatalogo, setLoadingCatalogo] = useState(false);
    const [estatus, setEstatus] = useState<flujoEnum>(flujoEnum.oculto); // Nuevo estado para el estatus
    const handleVerClick = (id: string, link: string) => {
        navigate("/" + link + "/alta/" + id);
    };
    const [modoEdicionTitulo, setModoEdicionTitulo] = useState(false);
    const [nuevoDocumento, setNuevoDocumento] = useState<File | null>(null);
    const [documentoPrevisualizacion, setDocumentoPrevisualizacion] = useState<string | null>(null);
    const [docNuevo, setDocNuevo] = useState(false);

    // Función para manejar el reemplazo del documento
    const reemplazarDocumento = () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'application/pdf';

        input.onchange = (e) => {
            const file = (e.target as HTMLInputElement).files?.[0];
            if (file) {
                // Validar que sea PDF
                if (file.type !== 'application/pdf') {
                    Swal.fire("Error", "Solo se permiten archivos PDF", "error");
                    return;
                }
                // Crear URL para previsualización
                const url = URL.createObjectURL(file);
                setNuevoDocumento(file);
                setDocNuevo(true);
                setDocumentoPrevisualizacion(url);
                // Limpiar la URL anterior si existe
                if (pdfUrl) {
                    URL.revokeObjectURL(pdfUrl);
                }
            }
        };
        input.click();
    };

    useEffect(() => {
        if (!id) {
            console.error('No se encontró ID en la URL');
            navigate('/admin/participaciones/federales/acuerdos', { replace: true });
            return;
        }
    }, [id, navigate]);

    const flujoDetalle = (accion: "avanzar" | "devolver") => {
        const tipo = "participaciones";
        const origen = "federales";

        FlujoAcuerdos(tipo, origen, setLoading, setResponseData, setError, estatus, id || "", accion);
    };

    const editar = async () => {
        const tipo = "participaciones";
        const origen = "federales";
        const datosParaGuardar = estatus === "DEVUELTO"
            ? {
                titulo,
                tipo_publicacion: tipoPublicacion,
                doc_nuevo: docNuevo,
                file: docNuevo ? nuevoDocumento : null
            }
            : { titulo };
        try {
            await EditarAcuerdo(
                tipo,
                origen,
                setLoading,
                setResponseData,
                obtenerDetalleAcuerdos,
                setError,
                id || "",
                datosParaGuardar,
                estatus
            );
            if (docNuevo) {
                setDocNuevo(false);
                setNuevoDocumento(null);
            }
        } catch (err: any) {
            console.error("Error completo:", err); // Verifica esto en consola
            let mensaje: string;

            // 1. Verificar si es un error de Multer (directo, no envuelto en Axios)
            if (typeof err === 'object' && err !== null && 'code' in err && err.code === 'LIMIT_FILE_SIZE') {
                mensaje = "El archivo excede el tamaño máximo permitido. Por favor, sube un archivo más pequeño.";
            }
            // 2. Verificar si es un error de Axios que podría contener un error de Multer
            else if (axios.isAxiosError(err)) {
                // Verificar si el error de Multer viene en la respuesta
                if (err.response?.data?.code === 'LIMIT_FILE_SIZE') {
                    mensaje = "El archivo excede el tamaño máximo permitido.";
                } else {
                    mensaje = err.response?.data?.message ||
                        err.response?.data?.error ||
                        err.message ||
                        "Error al procesar la solicitud";
                }
            }
            // 3. Otros tipos de errores
            else if (err instanceof Error) {
                mensaje = err.message;
            }
            else {
                mensaje = "Ocurrió un error inesperado";
            }

            Swal.fire("Error", mensaje, "error");
            return false;
        }
    };



    const obtenerDetalleAcuerdos = useCallback(async () => {
        if (!id) return;
        try {
            const tipo = "participaciones";
            const origen = "federales";

            await obtenerDocumentoAcuerdos(
                tipo,
                origen,
                setLoading,
                setError,
                setTitulo,
                setTipoPublicacion,
                setEstatus,
                id,
                setPdfUrl
            );
            setDocumentoPrevisualizacion(null)
        } catch (error) {
            console.error("Error al obtener detalles:", error);
            setError("Error al cargar los datos del acuerdo");
        }
    }, [id]);

    useEffect(() => {
        obtenerDetalleAcuerdos();
        obtenerCatalogoTipoPublicacion();
        return () => {
            // Limpieza al desmontar el componente
            if (pdfUrl) {
                URL.revokeObjectURL(pdfUrl);
                setPdfUrl(null);
            }
        };
    }, []);

    const obtenerCatalogoTipoPublicacion = async () => {
        setLoadingCatalogo(true);
        try {
            const response = await axios.get(`${process.env.REACT_APP_APPLICATION_ENDPOINT}/public/consulta/tipopublicacion`, {
                headers: {
                    Authorization: localStorage.getItem("jwtToken") || "",
                }
            });
            if (response.data.success) {
                setCatalogoTiposPublicacion(response.data.data);
            }
        } catch (error) {
            console.error("Error al cargar catálogo:", error);
        } finally {
            setLoadingCatalogo(false);
        }
    };

    const getEstatusCorrecto = (estatus: string) => {

        switch (estatus) {
            case "REVISION":
                return "Revisión";
            case "VALIDACION":
                return "Validación";
            case "PUBLICADO":
                return "Publicado";
            case "DEVUELTO":
                return "Devuelto";
            default:
                return estatus; // En caso de que el estatus no esté en los casos anteriores
        }
    };

    const obtenerTextoBoton = () => {
        switch (estatus) {
            case "REVISION":
                return "Validar";
            case "VALIDACION":
                return "Publicar";
            case "PUBLICADO":
                return "Publicado"; // O puedes devolver un texto diferente si es necesario
            case "OCULTO":
                return "revisar";
            case "DEVUELTO":
                return "revisar";
            default:
                return "Guardar"; // Si no hay estatus, por defecto
        }
    };
    
    const TITULO = estatus ? `${getEstatusCorrecto(estatus)} de Acuerdos` : "Validación de Acuerdos";

    return (

        <Grid container sx={{}}>
            <Grid item mb={2} width={"100%"}>
                <BreadcrumbBar
                    links={[
                        { texto: "Inicio", link: "/admin" },
                        { texto: "Acuerdos", link: "/admin/participaciones/federales/acuerdos" },
                    ]}
                    Titulo={TITULO}
                />
            </Grid>

            <Grid item mb={2} width={"100%"} >
                <Typography
                    variant="h5"
                    component="div"
                    sx={{
                        flexGrow: 1,
                        fontWeight: "bold",
                        display: "flex",
                        justifyContent: "left",

                    }}
                >
                    {estatus ? `${getEstatusCorrecto(estatus)} De Acuerdos` : "De Acuerdos"}
                </Typography>


                <Grid item width={"100%"} sx={{ mb: 2 }}>
                    {estatus === "DEVUELTO" && modoEdicionTitulo ? (
                        <TextField
                            variant="outlined"
                            fullWidth
                            value={titulo}
                            onChange={(e) => setTitulo(e.target.value)}
                            sx={{
                                fontWeight: "bold",
                            }}
                        />
                    ) : (
                        <Typography
                            variant="h5"
                            component="div"
                            sx={{
                                flexGrow: 1,
                                fontWeight: "bold",
                                display: "flex",
                                justifyContent: "left",
                            }}
                        >
                            {titulo}
                        </Typography>
                    )}
                </Grid>

                <Grid item xs={12} md={4}>
                    {estatus === "DEVUELTO" && modoEdicionTitulo ? (
                        <FormControl fullWidth>
                            <Autocomplete
                                id="tipo-publicacion"
                                options={catalogoTiposPublicacion}
                                getOptionLabel={(option) => option.nombre}
                                value={
                                    catalogoTiposPublicacion.find((p) => p.id === tipoPublicacion) || null
                                }
                                onChange={(_, newValue) => {
                                    setTipoPublicacion(newValue?.id || "");
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Tipo de Publicación"
                                        placeholder={
                                            catalogoTiposPublicacion.find(p => p.id === tipoPublicacion)?.nombre ||
                                            "Selecciona un tipo"
                                        }
                                    />
                                )}
                                disabled={loading}
                                loading={loadingCatalogo}
                            />
                        </FormControl>
                    ) : (
                        <Typography variant="body1" sx={{ mt: 1 }}>
                            <strong>Tipo de Publicación:</strong>{" "}
                            {catalogoTiposPublicacion.find(p => p.id === tipoPublicacion)?.nombre ||
                                (tipoPublicacion || "No especificado")}
                        </Typography>
                    )}
                </Grid>

                <Grid
                    item
                    xs={12}
                    md="auto"
                    display="flex"
                    justifyContent={{ xs: "center", md: "flex-start" }}
                    width="100%"
                >
                    {estatus === "DEVUELTO" && modoEdicionTitulo && (
                        <Button
                            variant="outlined"
                            color="secondary"
                            onClick={reemplazarDocumento}
                            sx={{ mt: 2 }}
                        >
                            REEMPLAZAR DOCUMENTO
                        </Button>
                    )}
                </Grid>
            </Grid>

            <Grid
                item
                xs={12}
                md="auto"
                display="flex"
                justifyContent={{ xs: "center", md: "flex-end" }}
                width="100%"
            >
                {estatus === "DEVUELTO" && (
                    <Button
                        onClick={() => {
                            if (modoEdicionTitulo) {
                                editar(); // Solo ejecuta editar cuando ya se está editando
                                setModoEdicionTitulo(false);
                            } else {
                                setModoEdicionTitulo(true); // Activa modo edición
                            }
                        }}
                        variant="contained"
                        color="primary"
                        fullWidth
                        sx={{
                            maxWidth: { xs: "100%", md: "auto" }, // ocupa todo el ancho en celular
                            marginBottom: "2rem"
                        }}
                    >
                        {modoEdicionTitulo ? "Guardar Acuerdo" : "Editar Acuerdo"}
                    </Button>
                )}
            </Grid>



            {/* Vista previa del PDF */}
            <Grid item width={"100%"} mb={2}>
                {documentoPrevisualizacion || pdfUrl ? (
                    <Box>
                        <iframe
                            src={documentoPrevisualizacion || pdfUrl || ''}
                            width="100%"
                            height="600px"
                            style={{ border: 'none' }}
                            title={`Documento: ${titulo}`}
                        />

                    </Box>
                ) : (
                    <p>{loading ? "Cargando documento..." : "Documento no disponible"}</p>
                )}
            </Grid>


            <Grid
                container
                spacing={2}
                sx={{ mt: 2 }}
                justifyContent={{ xs: "center", md: "flex-end" }}
            >
                <Grid
                    item
                    xs={12}
                    md="auto"
                    display="flex"
                    justifyContent={{ xs: "center", md: "flex-end" }}
                >
                    <Button
                        variant="contained"
                        sx={{
                            width: { xs: "100%", md: "7rem" },
                            textTransform: "capitalize",
                        }}
                        onClick={() => navigate("/admin/participaciones/federales/acuerdos")}
                        style={{ backgroundColor: "#C1A25C" }}
                    >
                        Cancelar
                    </Button>
                </Grid>

                {puedeActualizar(estatus) && (
                    <Grid
                        item
                        xs={12}
                        md="auto"
                        display="flex"
                        justifyContent={{ xs: "center", md: "flex-end" }}
                    >
                        <Button
                            variant="contained"
                            color="primary"
                            sx={{
                                width: { xs: "100%", md: "7rem" },
                                textTransform: "capitalize",
                            }}
                            disabled={
                                estatus === "OCULTO" ||
                                estatus === "DEVUELTO"
                            }
                            onClick={() => flujoDetalle("devolver")}
                        >
                            Devolver
                        </Button>
                    </Grid>
                )}

                {puedeActualizar(estatus) && (
                    <Grid
                        item
                        xs={12}
                        md="auto"
                        display="flex"
                        justifyContent={{ xs: "center", md: "flex-end" }}
                    >
                        <Button
                            variant="contained"
                            color="primary"
                            sx={{
                                width: { xs: "100%", md: "7rem" },
                                textTransform: "capitalize",
                            }}
                            disabled={
                                estatus === "PUBLICADO"
                            }
                            onClick={() => flujoDetalle("avanzar")}
                        >
                            {obtenerTextoBoton()}
                        </Button>
                    </Grid>
                )}
            </Grid>

        </Grid >
    );
};

export default VerifiacionAcuerdosPF;
